// frontend/src/components/ModeIndicator.tsx
// Компактный индикатор режима работы системы (автоматический/ручной)
// Версия: 2.1.0 - Props-based интерфейс
import React from 'react';
import { Chip, Tooltip, CircularProgress, Box } from '@mui/material';
import {
  AutoMode as AutoModeIcon,
  PanTool as ManualIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';

type ModeType = 'auto' | 'manual' | 'unknown';

interface ModeIndicatorProps {
  /** Текущий режим работы */
  mode: ModeType;
  /** Показывать текстовую метку */
  showLabel?: boolean;
  /** Размер компонента */
  size?: 'small' | 'medium';
  /** Загрузка данных */
  loading?: boolean;
  /** Информация о последнем изменении (для tooltip) */
  lastUpdated?: {
    by: string;
    at: string;
  };
}

/**
 * Индикатор текущего режима подтверждения заявок
 * Показывается операторам (только для чтения)
 */
export const ModeIndicator: React.FC<ModeIndicatorProps> = ({
  mode,
  showLabel = true,
  size = 'small',
  loading = false,
  lastUpdated,
}) => {
  // Форматирование даты для tooltip
  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Формирование текста tooltip
  const getTooltipText = (): string => {
    let text = '';

    switch (mode) {
      case 'auto':
        text =
          '🤖 АВТОМАТИЧЕСКИЙ режим\n\nЗаявки подтверждаются автоматически при совпадении номера терминала сигнала с номером SSAS в заявке.';
        break;
      case 'manual':
        text =
          '👤 РУЧНОЙ режим\n\nОператор проверяет каждое совпадение и подтверждает заявки вручную.';
        break;
      default:
        text = '❓ Режим неизвестен\n\nНе удалось определить текущий режим работы системы.';
    }

    if (lastUpdated) {
      text += `\n\nИзменено: ${lastUpdated.by}\n${formatDate(lastUpdated.at)}`;
    }

    return text;
  };

  // Конфигурация для каждого режима
  const modeConfig = {
    auto: {
      icon: <AutoModeIcon fontSize={size === 'small' ? 'small' : 'medium'} />,
      label: 'АВТОМАТ',
      color: 'error' as const,
      variant: 'filled' as const,
    },
    manual: {
      icon: <ManualIcon fontSize={size === 'small' ? 'small' : 'medium'} />,
      label: 'РУЧНОЙ',
      color: 'primary' as const,
      variant: 'outlined' as const,
    },
    unknown: {
      icon: <UnknownIcon fontSize={size === 'small' ? 'small' : 'medium'} />,
      label: '?',
      color: 'warning' as const,
      variant: 'outlined' as const,
    },
  };

  const config = modeConfig[mode] || modeConfig.unknown;

  // Состояние загрузки
  if (loading) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
        <CircularProgress size={size === 'small' ? 16 : 20} />
      </Box>
    );
  }

  return (
    <Tooltip
      title={<span style={{ whiteSpace: 'pre-line' }}>{getTooltipText()}</span>}
      arrow
      placement="bottom"
    >
      <Chip
        icon={config.icon}
        label={showLabel ? config.label : undefined}
        size={size}
        color={config.color}
        variant={config.variant}
        sx={{
          ml: 1,
          fontWeight: 'bold',
          cursor: 'help',
          '& .MuiChip-icon': {
            color: mode === 'auto' ? 'white' : 'inherit',
          },
          ...(mode === 'auto' && {
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.4)' },
              '70%': { boxShadow: '0 0 0 6px rgba(46, 125, 50, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(46, 125, 50, 0)' },
            },
          }),
        }}
      />
    </Tooltip>
  );
};

export default ModeIndicator;
